import React, { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  createForm,
  deleteForm,
  duplicateForm,
  getAllForm,
} from 'services/api/modules/performance/appraisal/form'
import { useLocation, useNavigate } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import AdxPopper from 'components/reusable/AdxPopper'
import LoadingSkeleton from './LoadingSkeleton'
import AdxButton from 'components/custom/AdxButton'

const FormList = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  useEffect(() => {
    queryClient.invalidateQueries('allFormData')
    queryClient.resetQueries('formData')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { isLoading: createLoading } = useMutation((values) => createForm(values), {
    onSuccess: async () => {
      queryClient.invalidateQueries('allFormData')
    },
  })

  const { mutate: duplicateMutate } = useMutation((formId) => duplicateForm(formId), {
    onSuccess: async () => {
      queryClient.invalidateQueries('allFormData')
    },
  })

  const { isLoading: deleteLoading, mutate: deleteMutate } = useMutation((id) => deleteForm(id), {
    onSuccess: async () => {
      queryClient.invalidateQueries('allFormData')
      queryClient.resetQueries('formData')
    },
    onError: async (error) => {
      queryClient.invalidateQueries('allFormData')
    },
  })

  const { isLoading: formLoading, data } = useQuery('allFormData', async () => {
    const { data } = await getAllForm()
    return data
  })

  const [formList, setFormList] = useState([])
  const location = useLocation()

  useEffect(() => {
    setFormList(data?.form ?? [])
  }, [data])

  const handleCreate = () => {
    navigate(`${location.pathname}/form/create`)
  }

  const duplicateHandler = (formId) => {
    duplicateMutate(formId)
  }

  const deleteHandler = (formId) => {
    deleteMutate(formId)
  }

  const viewHandler = (formId) => {
    navigate(`${location.pathname}/form/preview/${formId}`)
  }

  const editHandler = (formId) => {
    navigate(`${location.pathname}/form/edit/${formId}`)
  }

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h4" color="dark-text.main" fontWeight="bold">
          Form
        </Typography>
        <Typography variant="body1" color="light-gray-text.main">
          Create or use ready made form template
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: 'white.main',
          border: 1,
          borderColor: 'gray-line.main',
          borderRadius: '4.5px',
        }}
      >
        {formLoading ? (
          <LoadingSkeleton />
        ) : (
          formList.map((i, index) => (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              py={2}
              px={3}
              sx={{ borderBottom: 1, borderColor: 'gray-line.main' }}
              key={index}
            >
              <Box display="flex" gap={1} alignItems="center">
                <Typography variant="body1" color="dark-text.main">
                  {i.title}
                </Typography>
                {i.is_template === 2 ? (
                  <AdxStatusBar
                    status={{
                      text: 'Template',
                      color: 'alternate.main',
                      bgColor: 'alternate.light',
                    }}
                  />
                ) : null}
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography
                  variant="subtitle1"
                  color="alternate.main"
                  onClick={() => viewHandler(i.id)}
                  disabled={deleteLoading || formLoading ? true : false}
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Preview
                </Typography>
                <AdxPopper buttonIcon="more_horiz" placement="bottom-start" sx={{ marginTop: 1 }}>
                  <Typography
                    variant="subtitle1"
                    onClick={() => duplicateHandler(i.id)}
                    disabled={deleteLoading || formLoading ? true : false}
                  >
                    Duplicate
                  </Typography>
                  {i.is_template !== 2 ? (
                    <Typography
                      variant="subtitle1"
                      color="alternate.main"
                      onClick={() => editHandler(i.id)}
                      disabled={deleteLoading || formLoading ? true : false}
                    >
                      Edit
                    </Typography>
                  ) : null}
                  {i.is_template !== 2 ? (
                    <Typography
                      variant="subtitle1"
                      color="error.main"
                      onClick={() => deleteHandler(i.id)}
                      disabled={deleteLoading || formLoading ? true : false}
                    >
                      {deleteLoading ? 'Deleting...' : 'Delete'}
                    </Typography>
                  ) : null}
                </AdxPopper>
                {/* <Icon onClick={() => viewHandler(i.id)} sx={{ color: "light-gray-text.main", cursor: "pointer" }}>chevron_right</Icon>} */}
              </Box>
            </Box>
          ))
        )}

        {createLoading ? (
          <Box display="flex" alignItems="center" justifyContent="space-between" py={2} px={3}>
            <Typography variant="body1" color="dark-text.main">
              Creating new form...
            </Typography>
          </Box>
        ) : null}
        <Box sx={{ p: 0.5 }}>
          <AdxButton
            variant={'ghost'}
            fullWidth
            startIcon={<AddIcon color="alternate" />}
            sx={{
              pl: 3,
              display: 'flex',
              justifyContent: 'center',
            }}
            onClick={() =>
              // navigate(`${location.pathname}/create-form`) belum ada route
              handleCreate()
            }
          >
            Add custom form
          </AdxButton>
        </Box>
      </Box>
    </Box>
  )
}

export default FormList
